/* eslint-disable react/prop-types */
import React from 'react';
import { FormattedMessage } from '../../../util/reactIntl';
import SearchFilters from '../SearchFilters/SearchFilters';
import { Heading } from '../../../components';

import css from './MainPanelHeader.module.css';
import classNames from 'classnames';

/**
 * MainPanelHeader component
 *
 * @component
 * @param {Object} props
 * @param {string} [props.className] - Custom class that extends the default class for the root element
 * @param {string} [props.rootClassName] - Custom class that overrides the default class for the root element
 * @param {React.Node} props.children - The children
 * @param {React.Node} props.sortByComponent - The sort by component
 * @param {boolean} props.isSortByActive - Whether the sort by is active
 * @param {boolean} props.listingsAreLoaded - Whether the listings are loaded
 * @param {number} props.resultsCount - The results count
 * @param {boolean} props.searchInProgress - Whether the search is in progress
 * @param {React.Node} props.noResultsInfo - The no results info
 * @param {string} [props.heading] - Page-specific heading, rendered as the h1.
 *   Set by the SEO landing pages (brand, brand+model, boat type). Absent on the
 *   plain search page, which keeps the generic h2.
 * @returns {JSX.Element}
 */
const MainPanelHeader = props => {
  const {
    rootClassName,
    className,
    children,
    sortByComponent,
    listingsAreLoaded,
    resultsCount,
    noResultsInfo,
    renderPriceComponent,
    handleResetAllFilters,
    onManageDisableScrolling,
    heading,
  } = props;

  const classes = classNames(rootClassName || css.root, className);

  return (
    <div className={classes}>
      {/*
        An SEO landing page states its own subject and is the page's h1 — these
        URLs exist to rank for "Oud Huijzer 740 Tender", so a generic heading
        tells a crawler nothing. Everywhere else this stays exactly as it was:
        the bare <Heading> renders an h2 (`as || 'h2'`), and the plain search
        page keeps the generic copy.
      */}
      {/* `as` only, not styledAs — the tag and text change, the styling does not. */}
      {heading ? (
        <Heading as="h1" className={css.heading}>
          {heading}
        </Heading>
      ) : (
        <Heading className={css.heading}>
          <FormattedMessage id="Global.boatsForSale" />
        </Heading>
      )}

      <div className={classNames(css.searchFilters)}>
        <SearchFilters
          renderPriceComponent={renderPriceComponent}
          onManageDisableScrolling={onManageDisableScrolling}
          sortByComponent={sortByComponent}
          handleResetAllFilters={handleResetAllFilters}
          resultsCount={resultsCount}
        />
      </div>
      <div className={css.searchFound}>
        <div>
          <span className={css.searchResults}>
            {listingsAreLoaded ? (
              <FormattedMessage
                id="MainPanelHeader.resultsFound"
                values={{
                  count: resultsCount,
                  b: chunks => <b>{chunks}</b>,
                }}
              />
            ) : (
              <FormattedMessage id="MainPanelHeader.loadingResults" />
            )}
          </span>
        </div>
      </div>
      {children}

      {noResultsInfo ? noResultsInfo : null}
    </div>
  );
};

export default MainPanelHeader;
